<h2 mat-dialog-title>{{ data.title || providerLabel + ' PAT' }}</h2>

<mat-dialog-content>
  <div class="dialog-content">
    @if (patStatus === 'loading') {
      <div class="text-center mt-3 mb-3">
        <app-spinner [isLoading]="true" diameter="32" loadingText="Loading..."></app-spinner>
      </div>
    }

    @if (patStatus !== 'loading' && (patStatus === 'missing' || showUpdatePat)) {
      @if (showUpdatePat) {
        <div class="alert alert-info mb-3">
          <div>
            <strong>Update your {{ providerLabel }} PAT</strong>
            <p class="mb-0 mt-1">Enter a new Personal Access Token below to replace your existing one.</p>
          </div>
        </div>
      } @else {
        <div class="alert alert-warning mb-3 d-flex align-items-start">
          <mat-icon class="mr-2 mt-1 flex-shrink-0" style="font-size:20px;">lock</mat-icon>
          <div>
            <strong>{{ providerLabel }} PAT required</strong>
            <p class="mb-0 mt-1">
              {{ data.intro || 'Approving modernization creates an Azure DevOps work item. Please configure your Personal Access Token first.' }}
            </p>
          </div>
        </div>
      }

      <div class="pat-instructions mb-3 p-3 border rounded bg-light">
        <strong>How to generate a PAT:</strong>
        @if (provider === 'github') {
          <ol class="mb-0 mt-1">
            <li>In GitHub, open <em>Settings &rarr; Developer settings &rarr; Personal access tokens</em></li>
            <li>Click <strong>Generate new token</strong> and set a name and expiry</li>
            <li>Grant the <strong>repo</strong> scope (read access to Chevron repositories)</li>
            <li>Authorize the token for SSO if your organization requires it</li>
            <li>Click <strong>Generate token</strong>, then copy it below</li>
          </ol>
        } @else {
          <ol class="mb-0 mt-1">
            <li>In Azure DevOps, click your profile icon (top-right) and open <em>Personal access tokens</em></li>
            <li>Click <strong>New Token</strong></li>
            <li>Set a name, choose your organization scope, and set an expiry</li>
            <li>Under <em>Scopes</em>, grant <strong>Code (Read)</strong> and <strong>Work Items (Read &amp; write)</strong></li>
            <li>Click <strong>Create</strong>, then copy the token below</li>
          </ol>
        }
        <div class="mt-2">
          <a href="/profile/user-guide?chapter=13" target="_blank" rel="noopener noreferrer">Open the Personal Access Token guide</a>
        </div>
      </div>

      <div class="form-group mb-3">
        <label class="form-label" for="patInput">
          Personal Access Token <span class="text-danger">*</span>
        </label>
        <div class="input-group">
          <input
            id="patInput"
            [type]="showPat ? 'text' : 'password'"
            class="form-control"
            [(ngModel)]="patInput"
            [disabled]="isSavingPat"
            [placeholder]="'Paste your ' + providerLabel + ' PAT here'"
            autocomplete="off">
          <button
            class="btn btn-outline-secondary"
            type="button"
            (click)="showPat = !showPat"
            [attr.aria-label]="showPat ? 'Hide PAT' : 'Show PAT'">
            {{ showPat ? 'Hide' : 'Show' }}
          </button>
        </div>
      </div>

      @if (patSaveError) {
        <div class="alert alert-danger mt-2">
          {{ patSaveError }}
        </div>
      }

      <div class="form-group mb-3">
        <label class="form-label" for="patExpiryPreset">Expiration (UTC)</label>
        <div class="d-flex align-items-end flex-wrap pat-expiry-fields">
          <select
            id="patExpiryPreset"
            class="form-control"
            [(ngModel)]="patExpiryPreset"
            (ngModelChange)="onPatExpiryPresetChange()"
            [disabled]="isSavingPat"
            style="max-width:220px;">
            <option value="">No expiration</option>
            <option value="30">30 days</option>
            <option value="60">60 days</option>
            <option value="90">90 days</option>
            <option value="custom">Custom date</option>
          </select>

          @if (patExpiryPreset === 'custom') {
            <input
              id="patExpiresAt"
              type="date"
              class="form-control"
              [(ngModel)]="patExpiresAtInput"
              [disabled]="isSavingPat"
              style="max-width:200px;">
          }
        </div>
      </div>

      <div class="d-flex justify-content-end">
        <button
          class="btn btn-primary"
          (click)="savePat()"
          [disabled]="!patInput.trim() || isSavingPat">
          @if (isSavingPat) {
            <app-spinner [isLoading]="true" diameter="18" loadingText="Saving..." [showText]="false"></app-spinner>
          } @else {
            Save PAT
          }
        </button>
      </div>
    }

    @if (patStatus === 'configured' && !showUpdatePat) {
      @if (patExpiryWarning === 'expired') {
        <div class="alert alert-danger mb-3">
          <strong>Your {{ providerLabel }} PAT has expired.</strong>
          <p class="mb-0 mt-1">Please update your PAT before continuing.</p>
        </div>
      }

      @if (patExpiryWarning === 'expiring-soon') {
        <div class="alert alert-warning mb-3">
          <strong>Your {{ providerLabel }} PAT will expire soon.</strong>
          <p class="mb-0 mt-1">
            It expires on {{ patExpiresAt | utcDate:'date' }}. Consider updating it now.
          </p>
        </div>
      }

      <div class="alert alert-success mb-3">
        <strong>PAT is configured.</strong>
        <p class="mb-0 mt-1">You can continue approval, or update the token before proceeding.</p>
      </div>
    }
  </div>
</mat-dialog-content>

<mat-dialog-actions>
  <button class="btn" (click)="onCancel()" [disabled]="isSavingPat">Cancel</button>
  <span style="flex: 1;"></span>

  @if (patStatus !== 'loading' && !showUpdatePat) {
    <button class="btn btn-outline-secondary mr-2" (click)="openUpdatePat()">Update PAT</button>
    <button class="btn" (click)="onContinue()" [disabled]="!canProceed()">Continue</button>
  }
</mat-dialog-actions>
